// Terminal-Verwaltung in den Werkstatt-Einstellungen (Phase 3, docs/DESIGN-zeit-kiosk-terminal.md).
// Der Chef legt ein Gerät an und bekommt den Kopplungs-Code GENAU EINMAL zu sehen — danach existiert nur
// noch dessen Hash. Verloren = neu anlegen, altes widerrufen.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, type KioskDeviceDTO } from '../api/client'

const fmt = (iso: string | null): string =>
  iso ? new Date(iso.replace(' ', 'T') + 'Z').toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—'

export function KioskDevicesCard(): React.JSX.Element {
  const qc = useQueryClient()
  const [label, setLabel] = useState('')
  const [freshToken, setFreshToken] = useState<{ label: string; token: string } | null>(null)
  const devices = useQuery({ queryKey: ['kioskDevices'], queryFn: () => api.kioskDevices() })

  const create = useMutation({
    mutationFn: () => api.createKioskDevice(label.trim()),
    onSuccess: (res) => {
      setFreshToken({ label: res.label, token: res.token })
      setLabel('')
      void qc.invalidateQueries({ queryKey: ['kioskDevices'] })
    },
  })

  const revoke = useMutation({
    mutationFn: (id: number) => api.revokeKioskDevice(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['kioskDevices'] }),
  })

  const list: KioskDeviceDTO[] = devices.data?.devices ?? []

  return (
    <section className="card">
      <h3>Terminals (Stempeluhr am Wandtablet)</h3>
      <p className="muted">
        Ein Terminal lässt Mitarbeiter mit ihrer persönlichen PIN stempeln, ohne sich anzumelden. Die PIN
        vergibt jeder Mitarbeiter selbst unter „Konto“ — du kannst sie zurücksetzen, aber nie einsehen.
      </p>

      <div className="wf-split">
        <input
          className="wf-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Name des Geräts, z. B. „Tablet Halle“"
          maxLength={60}
          aria-label="Name des Terminals"
        />
        <button type="button" className="wf-btn wf-btn--primary" disabled={!label.trim() || create.isPending} onClick={() => create.mutate()}>
          Terminal anlegen
        </button>
      </div>
      {create.isError && <p className="wf-error">{(create.error as Error).message}</p>}

      {freshToken && (
        <div className="wf-callout" role="status">
          <p>
            <b>{freshToken.label}</b> ist angelegt. Öffne am Tablet <code>/terminal</code> und gib diesen Code ein:
          </p>
          <p className="wf-kiosk__code">{freshToken.token}</p>
          <p className="muted">
            Der Code wird nur dieses eine Mal angezeigt. Wenn du ihn verlierst, widerrufe das Gerät und lege es neu an.
          </p>
          <button type="button" className="wf-btn" onClick={() => setFreshToken(null)}>Verstanden</button>
        </div>
      )}

      {list.length > 0 && (
        <table className="wf-table">
          <thead>
            <tr><th>Gerät</th><th>Angelegt</th><th>Zuletzt aktiv</th><th /></tr>
          </thead>
          <tbody>
            {list.map((d) => (
              <tr key={d.id} className={d.revokedAt ? 'wf-row--muted' : undefined}>
                <td>{d.label}</td>
                <td>{fmt(d.createdAt)}</td>
                <td>{fmt(d.lastSeenAt)}</td>
                <td>
                  {d.revokedAt ? (
                    <span className="muted">widerrufen {fmt(d.revokedAt)}</span>
                  ) : (
                    <button type="button" className="wf-btn wf-btn--sm" onClick={() => revoke.mutate(d.id)} disabled={revoke.isPending}>
                      Widerrufen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
